import React from 'react';
import { BarChart3, TrendingUp, Users, MousePointerClick, CheckCircle2 } from 'lucide-react';
import { ModulePlaceholder } from '@/components/modules/common/ModulePlaceholder';
import { usePartnerOverview } from '@/hooks/usePartnerOverview';

export const AnalyticsView: React.FC = () => {
  const { data } = usePartnerOverview();

  return (
    <ModulePlaceholder
      title="Performance & Conversion Analytics"
      subtitle="Track your referral click-throughs, signups, and customer conversion rates"
      icon={BarChart3}
      badge="Performance Analytics"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-[#0E1015] border border-zinc-800 p-5 rounded-2xl">
            <div className="flex items-center justify-between text-zinc-400 mb-2">
              <span className="text-xs font-medium uppercase">Total Link Clicks</span>
              <MousePointerClick className="w-4 h-4 text-indigo-400" />
            </div>
            <div className="text-2xl font-bold text-white mt-1">
              {data.totalClicks.toLocaleString()}
            </div>
            <p className="text-xs text-zinc-400 mt-2">Visits via referral links</p>
          </div>

          <div className="bg-[#0E1015] border border-zinc-800 p-5 rounded-2xl">
            <div className="flex items-center justify-between text-zinc-400 mb-2">
              <span className="text-xs font-medium uppercase">Merchant Leads</span>
              <Users className="w-4 h-4 text-blue-400" />
            </div>
            <div className="text-2xl font-bold text-white mt-1">
              {data.totalLeads.toLocaleString()}
            </div>
            <p className="text-xs text-zinc-400 mt-2">Account registrations</p>
          </div>

          <div className="bg-[#0E1015] border border-zinc-800 p-5 rounded-2xl">
            <div className="flex items-center justify-between text-zinc-400 mb-2">
              <span className="text-xs font-medium uppercase">Active Subscriptions</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="text-2xl font-bold text-white mt-1">
              {data.activeSubscriptions.toLocaleString()}
            </div>
            <p className="text-xs text-zinc-400 mt-2">Paid customer plans</p>
          </div>

          <div className="bg-[#0E1015] border border-zinc-800 p-5 rounded-2xl">
            <div className="flex items-center justify-between text-zinc-400 mb-2">
              <span className="text-xs font-medium uppercase">Conversion Rate</span>
              <TrendingUp className="w-4 h-4 text-amber-400" />
            </div>
            <div className="text-2xl font-bold text-white mt-1">
              {data.conversionRatePercent.toFixed(1)}%
            </div>
            <p className="text-xs text-zinc-400 mt-2">Click to paid conversion</p>
          </div>
        </div>

        {/* Funnel Overview */}
        <div className="bg-[#0E1015] border border-zinc-800 rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-white mb-2">Attribution Pipeline</h2>
          <p className="text-xs text-zinc-400 mb-6">
            Merchants who click your link are tracked for 60 days. When they upgrade to any paid Conversora plan, you earn {data.commissionRatePercent}% recurring commissions.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wider block mb-1">
                Step 1: Link Click
              </span>
              <p className="text-zinc-200 font-medium">Merchant clicks your custom URL</p>
              <p className="text-zinc-400 mt-1">60-day attribution cookie is attached.</p>
            </div>

            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wider block mb-1">
                Step 2: Merchant Signs Up
              </span>
              <p className="text-zinc-200 font-medium">Registers their Conversora store</p>
              <p className="text-zinc-400 mt-1">Permanent partner attribution is locked.</p>
            </div>

            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider block mb-1">
                Step 3: Earn Monthly Revenue
              </span>
              <p className="text-zinc-200 font-medium">{data.commissionRatePercent}% Recurring Commission</p>
              <p className="text-zinc-400 mt-1">Credited directly to your balance on renewals.</p>
            </div>
          </div>
        </div>
      </div>
    </ModulePlaceholder>
  );
};

export default AnalyticsView;
